import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { authAPI } from '../../services/api';

interface EmailVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  email: string;
}

export const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  email,
}) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleResend = async () => {
    setResending(true);
    setError('');
    setSuccess('');

    try {
      await authAPI.resendVerificationEmail(email);
      setSuccess('Verification email resent successfully. Please check your email.');
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        setError(errorDetail);
      } else {
        setError('Failed to resend email. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await authAPI.verifyEmail(email, code);
      setLoading(false);
      onSuccess();
    } catch (err: any) {
      setLoading(false);
      const errorDetail = err.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        setError(errorDetail);
      } else {
        setError('Verification failed. Please check your code and try again.');
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Email Verification" size="sm">
      <div className="space-y-4">
        <div className="bg-emerald-50 p-4 rounded-lg text-sm text-emerald-900">
          Please check your email for the verification code.
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Enter Verification Code"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter 6-digit code"
            maxLength={6}
            required
          />

          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="text-sm text-green-600 bg-green-50 p-3 rounded-lg">
              {success}
            </div>
          )}

          <div className="flex space-x-3">
            <Button
              type="button"
              variant="secondary"
              onClick={handleResend}
              fullWidth
              disabled={resending}
            >
              {resending ? 'Resending...' : 'Resend Email'}
            </Button>
            <Button type="submit" fullWidth disabled={loading}>
              {loading ? 'Verifying...' : 'Next'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
